/**
 * Logger utility for consistent logging across the application
 * with support for different log levels and environments
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  level: LogLevel;
  enablePerformanceLogs: boolean;
  minDurationToLog: number; // in milliseconds
}

const config: LoggerConfig = {
  level: process.env.NODE_ENV === 'development' ? 'info' : 'info',
  enablePerformanceLogs: false,
  minDurationToLog: 16.67, // Log renders slower than one frame (60fps)
};

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
}

function formatMessage(level: LogLevel, message: string, data?: any): string {
  const timestamp = new Date().toISOString();
  const dataString = data ? `\n${JSON.stringify(data, null, 2)}` : '';
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${dataString}`;
}

// Performance specific logging
function logPerformance(component: string, phase: string, duration: number, data?: any) {
  if (!config.enablePerformanceLogs) return;
  
  const level = duration > config.minDurationToLog ? 'error' : 'debug';
  const message = `[PERFORMANCE] [${component}] ${phase} - ${duration.toFixed(2)}ms`;
  
  logger[level](message, {
    component,
    phase,
    duration,
    ...data,
  });
}

const logger = {
  debug: (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development' && shouldLog('debug')) {
      console.debug(message, data);
    }
  },

  info: (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development' && shouldLog('info')) {
      console.info(message, data);
    }
  },

  warn: (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development' && shouldLog('warn')) {
      console.warn(message, data);
    }
  },

  error: (message: string, error?: any) => {
    if (shouldLog('error')) {
      console.error(message, error);
    }
  },

  performance: {
    logOperation: (id: string, phase: string, duration: number, metrics?: any) => {
      if (process.env.NODE_ENV === 'development' && config.enablePerformanceLogs) {
        console.info(`[${id}] ${phase} took ${duration}ms`, metrics);
      }
    },
  },
};

export { logger };
